import { useEffect } from 'react';
import type { GuideTourId } from '../content/tours';
import { useGuideStore } from '../store/guideStore';
import { useGuide } from './useGuide';

/**
 * Fires the screen's tour the first time the manager gets there, and ends it
 * when they leave. Screens call this unconditionally; the store decides
 * whether it runs.
 */
export function useAutoStartTour(tourId: GuideTourId) {
  const { start, stop } = useGuide();
  const enabled = useGuideStore((state) => state.enabled);

  useEffect(() => {
    if (!enabled) return;
    // Read imperatively: marking this tour seen at the end must not re-run the
    // effect and restart it, and subscribing would re-render every screen on
    // every completed tour.
    if (useGuideStore.getState().seenTours.includes(tourId)) return;
    start(tourId);
  }, [tourId, enabled, start]);

  // The tour belongs to this screen whoever opened it, the launcher included.
  // Left running after the manager went back, it waited hidden for its
  // anchors and came back with the screen, even with the guides turned off.
  useEffect(() => () => stop(tourId), [tourId, stop]);
}
